import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, withQuery } from '../../core/client';
import { growRoomKeys } from '../../core/query-keys';
import { growRoomEndpoints } from './grow-rooms.endpoints';
import type { GrowRoom, CreateGrowRoomRequest, UpdateGrowRoomRequest } from './grow-rooms.types';

export function useGrowRooms(codeContains = '') {
  return useQuery({
    queryKey: [...growRoomKeys.list, codeContains],
    queryFn: async () =>
      (
        await api.get<{ grow_rooms: GrowRoom[]; next_page_token: string }>(
          withQuery(growRoomEndpoints.list, { grow_room_code_contains: codeContains }),
        )
      ).grow_rooms ?? [],
  });
}

export function useGrowRoom(id: string) {
  return useQuery({
    queryKey: growRoomKeys.one(id),
    queryFn: () => api.get<GrowRoom>(growRoomEndpoints.one(id)),
    enabled: Boolean(id),
  });
}

export function useCreateGrowRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CreateGrowRoomRequest) => api.post<GrowRoom>(growRoomEndpoints.list, req),
    onSuccess: () => qc.invalidateQueries({ queryKey: growRoomKeys.all }),
  });
}

export function useUpdateGrowRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...req }: UpdateGrowRoomRequest & { id: string }) =>
      api.patch<GrowRoom>(growRoomEndpoints.one(id), req),
    onSuccess: () => qc.invalidateQueries({ queryKey: growRoomKeys.all }),
  });
}

export function useDeleteGrowRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(growRoomEndpoints.one(id)),
    onSuccess: () => qc.invalidateQueries({ queryKey: growRoomKeys.all }),
  });
}
